import { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Phone, Menu, X, Globe } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { Logo } from "./Logo";
import { LANGS, useI18n } from "../../lib/i18n";
import { PHONE, PHONE_DISPLAY, scrollToId } from "../../lib/api";

const LangSwitch = ({ testid }) => {
  const { lang, setLang } = useI18n();
  return (
    <div className="flex items-center gap-1 rounded-full border border-ink/10 bg-white/70 p-1" data-testid={testid}>
      <Globe className="mx-1 h-3.5 w-3.5 text-slate-500" />
      {LANGS.map((l) => (
        <button
          key={l.code}
          onClick={() => setLang(l.code)}
          data-testid={`${testid}-${l.code}`}
          title={l.name}
          className={`rounded-full px-2 py-1 font-mono text-[11px] tracking-wider transition-colors duration-300 ${lang === l.code ? "bg-ink text-pearl" : "text-slate-600 hover:text-ink"}`}
        >
          {l.label}
        </button>
      ))}
    </div>
  );
};

export const Navbar = () => {
  const { t } = useI18n();
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const links = [["inventory", t.nav.inventory], ["services", t.nav.services], ["contact", t.nav.contact]];

  const go = (id) => {
    setOpen(false);
    if (pathname === "/") scrollToId(id);
    else navigate(`/#${id}`);
  };

  return (
    <header className="fixed inset-x-0 top-0 z-50 border-b border-ink/5 bg-pearl/80 backdrop-blur-xl" data-testid="site-navbar">
      <div className="mx-auto flex max-w-[1440px] items-center justify-between px-5 py-3.5 sm:px-8 lg:px-12">
        <Link to="/" data-testid="navbar-logo"><Logo /></Link>
        <nav className="hidden items-center gap-9 lg:flex">
          {links.map(([id, label]) => (
            <button key={id} onClick={() => go(id)} data-testid={`nav-${id}`} className="link-underline text-sm font-medium text-slate-700 hover:text-ink">
              {label}
            </button>
          ))}
        </nav>
        <div className="hidden items-center gap-3 lg:flex">
          <LangSwitch testid="language-switcher" />
          <a href={`tel:${PHONE}`} data-testid="nav-call-button" className="btn-ink group">
            <Phone className="h-4 w-4 transition-transform duration-500 group-hover:rotate-12" /> {PHONE_DISPLAY}
          </a>
        </div>
        <button className="rounded-full border border-ink/10 p-2.5 lg:hidden" onClick={() => setOpen((v) => !v)} data-testid="mobile-menu-toggle" aria-label="Menu">
          {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
        </button>
      </div>
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="overflow-hidden border-t border-ink/5 bg-pearl lg:hidden"
            data-testid="mobile-menu"
          >
            <div className="flex flex-col gap-1 px-5 py-6">
              {links.map(([id, label]) => (
                <button key={id} onClick={() => go(id)} data-testid={`mobile-nav-${id}`} className="py-3 text-left font-display text-3xl font-extrabold uppercase text-ink">
                  {label}
                </button>
              ))}
              <div className="mt-4"><LangSwitch testid="mobile-language-switcher" /></div>
              <a href={`tel:${PHONE}`} className="btn-ink mt-4 justify-center" data-testid="mobile-call-button">
                <Phone className="h-4 w-4" /> {PHONE_DISPLAY}
              </a>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
};
